import { memo, useMemo } from 'react'
import { observer } from 'mobx-react'
import {
  Typography,
  createStyles,
  withStyles,
  makeStyles
} from '@material-ui/core'
import LockIcon from '@material-ui/icons/Lock'
import { isConfigSmava } from 'utils/url'
import { Debtor } from '../../../containers/PersonalForm/PersonalForm.interface'
import TraversalStore from '../../../stores/TraversalStore'
import { useStores } from '../../../stores/utils/hooks/useStores'
import Notice from '../../Notice'

const StyledHeading = withStyles((theme) =>
  createStyles({
    root: {
      marginBottom: theme.spacing(2)
    }
  })
)(Typography)

const StyledDefaultValue = withStyles(() =>
  createStyles({
    root: {
      margin: 0
    }
  })
)(Typography)

const useStyles = makeStyles((theme) =>
  createStyles({
    infoList: {
      listStyleType: 'none',
      padding: 0,
      margin: 0,
      marginBottom: theme.spacing(2)
    },
    infoListItem: {
      display: 'flex',
      flexDirection: 'row',
      paddingTop: theme.spacing(2)
    },
    infoText: {
      marginLeft: theme.spacing(2)
    },
    title: {
      fontWeight: 'bold'
    }
  })
)

const getPersonalFactory =
  (traversal: TraversalStore, debtor: Debtor) => (property: string) =>
    traversal.getFieldValueByPath(`debtors.${debtor}.personal.${property}`)

const getFullName = (traversal: TraversalStore) => (debtor: Debtor) => {
  const personal = getPersonalFactory(traversal, debtor)

  return `${personal('firstname') || ''} ${personal('lastname') || ''}`
}

const TextRender = memo(() => {
  const { traversal } = useStores()
  const defaultValue = useMemo(
    () => getFullName(traversal)(Debtor.PRIMARY),
    [traversal]
  )
  return (
    <>
      <StyledHeading variant="h4">Kontoinhaber</StyledHeading>
      <StyledDefaultValue variant="body2" data-hj-suppress>
        {defaultValue}
      </StyledDefaultValue>
    </>
  )
})

TextRender.displayName = 'TextRender'

const AccountHolder = observer(() => {
  const { infoList, infoListItem, infoText, title } = useStyles()
  const isSmavaConfig = isConfigSmava()

  return (
    <>
      <Notice type="info">
        <Typography variant="h5" className={title}>
          {isSmavaConfig
            ? 'Datensicherheit: Wussten Sie...'
            : 'Ihre Kontobewegungen werden nicht überprüft'}
        </Typography>
        <ul className={infoList}>
          {isSmavaConfig && (
            <li className={infoListItem}>
              <LockIcon />
              <Typography variant="body2" className={infoText}>
                Von Ihrem Konto wird kein Geld abgebucht.
              </Typography>
            </li>
          )}
          {!isSmavaConfig && (
            <li className={infoListItem}>
              <Typography variant="body2">
                Die IBAN wird benötigt, um Sie als Person zu verifizieren. So
                erhalten Sie reale Angebote. Von Ihrem Konto wird kein Geld
                abgebucht.
              </Typography>
            </li>
          )}
        </ul>
      </Notice>
      <TextRender />
    </>
  )
})

export default memo(AccountHolder)
